/**
 * Personal Modality Pie Chart Component
 *
 * Displays distribution of user's work by modality
 * Uses centralized dashboard config for titles
 */

import { Loader2 } from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import { useMyStudiesByModality } from '@/application/dashboard'
import { MODALITY_LABELS, type Modality } from '@/config/constants'
import { getModalityChartTitle } from '@/config/dashboard.config'
import { useAuth } from '@/application/auth'

// Chart colors (matching the primary color palette)
const COLORS = [
  '#0ea5e9', // sky-500
  '#8b5cf6', // violet-500
  '#22c55e', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#06b6d4', // cyan-500
  '#ec4899', // pink-500
  '#84cc16', // lime-500
  '#f97316', // orange-500
  '#6366f1', // indigo-500
]

interface CustomTooltipProps {
  active?: boolean
  payload?: Array<{
    name: string
    value: number
    payload: { modality: string; count: number; percentage: number }
  }>
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    const label = MODALITY_LABELS[data.modality as Modality] || data.modality
    return (
      <div className="bg-[var(--background)] border border-[var(--border)] rounded-lg p-3 shadow-lg">
        <p className="font-medium text-[var(--foreground)]">{label}</p>
        <p className="text-sm text-[var(--foreground-muted)]">
          {data.count} ({data.percentage}%)
        </p>
      </div>
    )
  }
  return null
}

export function PersonalModalityPieChart() {
  const { user } = useAuth()
  const { data, isLoading, isError } = useMyStudiesByModality()

  // Get title from centralized config
  const title = getModalityChartTitle(user?.role)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px]">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartData = data.distribution.map((item, index) => ({
    ...item,
    name: MODALITY_LABELS[item.modality as Modality] || item.modality,
    fill: COLORS[index % COLORS.length],
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-[250px] text-[var(--foreground-muted)]">
            Sin datos disponibles
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={2}
                dataKey="count"
                nameKey="name"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(value) => (
                  <span className="text-sm text-[var(--foreground)]">{value}</span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
